import { Component, ReactNode } from 'react';
import { logger } from '@client/common/utils/logger/logger';
import { AppCrash } from './AppCrash';
import { Props, State } from './';

export const DOM_NODE_MUTATION_ERRORS = [
  "Failed to execute 'insertBefore'",
  "Failed to execute 'removeChild'",
  'Node.insertBefore',
  'Node.removeChild',
  'The object can not be found here',
];

export const isDomNodeMutationError = (message: unknown): boolean => {
  const text = typeof message === 'string' ? message : '';

  return DOM_NODE_MUTATION_ERRORS.some((pattern) => text.includes(pattern));
};

const getCaughtErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return String(error ?? '');
};

export class ErrorBoundary extends Component<Props, State> {
  public state: { error: Error | null } = {
    error: null,
  };

  public componentDidCatch(error: Error, errorInfo: Record<string, any> = {}): void {
    const message = getCaughtErrorMessage(error);

    if (isDomNodeMutationError(message)) {
      logger.warn(
        message,
        {
          error,
          ...errorInfo,
          source: 'ErrorBoundary',
          tags: {
            from_boundary: 'true',
          },
        },
      );
      return;
    }

    logger.error(
      message,
      {
        error,
        ...errorInfo,
        source: 'ErrorBoundary',
        tags: {
          from_boundary: 'true',
          app_crash: 'true',
        },
        handled: false,
      },
    );

    this.setState({
      error: error instanceof Error ? error : new Error(message),
    });
  }

  public render(): ReactNode {
    if (this.state.error) {
      return <AppCrash />;
    }

    return this.props.children;
  }
}
